/**
 * Color Utility Functions for adjusting lightness while preserving hue and saturation
 */

/**
 * Converts a hex color to HSL color space
 * @param {string} hex - Color in hex format (e.g., "#FF0000" or "#F00")
 * @returns {Object} Object containing h, s, l values
 */
export function hexToHSL(hex) {
  // Remove the # if present
  hex = hex.replace(/^#/, "");

  // Parse the hex values
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16) / 255;
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16) / 255;
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16) / 255;
  } else {
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  }

  // Find the minimum and maximum values to calculate the lightness
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  // Calculate lightness
  const lightness = (max + min) / 2;

  // Calculate saturation
  let saturation = 0;
  if (max !== min) {
    saturation =
      lightness > 0.5
        ? (max - min) / (2 - max - min)
        : (max - min) / (max + min);
  }

  // Calculate hue
  let hue = 0;
  if (max !== min) {
    if (max === r) {
      hue = (g - b) / (max - min) + (g < b ? 6 : 0);
    } else if (max === g) {
      hue = (b - r) / (max - min) + 2;
    } else if (max === b) {
      hue = (r - g) / (max - min) + 4;
    }
    hue /= 6;
  }

  // Return HSL values
  return {
    h: Math.round(hue * 360),
    s: Math.round(saturation * 100),
    l: Math.round(lightness * 100),
  };
}

/**
 * Converts HSL color values to hex format
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Color in hex format (e.g., "#FF0000")
 */
export function hslToHex(h, s, l) {
  // Convert HSL percentages to decimal
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r, g, b;

  if (s === 0) {
    // If saturation is 0, it's a shade of gray
    r = g = b = l;
  } else {
    // Helper function to calculate RGB components
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  // Convert RGB to hex
  const toHex = (x) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Adjusts a color to a specific lightness while preserving its hue and saturation
 * If the color is already lighter than the target lightness, it returns unchanged
 * @param {string} hexColor - Color in hex format (e.g., "#FF0000")
 * @param {number} targetLightness - Target lightness value (0-100)
 * @returns {string} Adjusted color in hex format
 */
export function adjustColorLightness(hexColor, targetLightness) {
  // Convert the hex color to HSL
  const hsl = hexToHSL(hexColor);

  // If the current lightness is already higher than the target, return the original color
  if (hsl.l >= targetLightness) {
    return hexColor;
  }

  // Otherwise, adjust the lightness to the target value and convert back to hex
  return hslToHex(hsl.h, hsl.s, targetLightness);
}

/**
 * Returns a very light version of the provided color
 * If the color is already lighter than the target lightness, it returns unchanged
 * @param {string} hexColor - Color in hex format (e.g., "#FF0000")
 * @param {number} lightness - Target lightness (default: 90)
 * @returns {string} Lightened color in hex format
 */
export function getLightVersion(hexColor, lightness = 90) {
  return adjustColorLightness(hexColor, lightness);
}

/**
 * Returns a light version of the provided color
 * @param {string} hexColor - Color in hex format
 * @returns {string} Light version in hex format
 */
export function getVeryLightVersion(hexColor) {
  return getLightVersion(hexColor, 90);
}

/**
 * Returns a moderately light version of the provided color
 * @param {string} hexColor - Color in hex format
 * @returns {string} Moderately light version in hex format
 */
export function getLightishVersion(hexColor) {
  return getLightVersion(hexColor, 75);
}

/**
 * Returns a medium light version of the provided color
 * @param {string} hexColor - Color in hex format
 * @returns {string} Medium light version in hex format
 */
export function getMediumLightVersion(hexColor) {
  return getLightVersion(hexColor, 60);
}

/**
 * Usage examples:
 *
 * // Get a very light version (90% lightness)
 * const lightRed = getVeryLightVersion('#FF0000');  // Returns #ffcccc
 *
 * // Get a custom lightness version
 * const customLight = getLightVersion('#123456', 85);  // Returns a color with 85% lightness
 *
 * // Colors that are already lighter than the target are returned unchanged
 * const alreadyLight = getVeryLightVersion('#EEEEEE');  // Returns #EEEEEE (unchanged)
 */
